import { categoryIcon } from './categoryIcons';
import type { Section } from './icons';

/**
 * The categories, as something you can tap.
 *
 * A select box lists what exists; a grid says what the section is FOR. Somebody
 * arriving at Services does not know whether NyumbaSuite covers a gas delivery
 * or a mama fua until they open a dropdown and read seventeen options — which
 * is a thing nobody does on a phone. Laid out as tiles, the same seventeen
 * answer the question at a glance and are a single tap rather than a tap, a
 * scroll and a choice.
 *
 * EMPTY CATEGORIES ARE SHOWN, quietly. Hiding them would make the section look
 * complete and tell a plumber looking for somewhere to list that there is no
 * room for them; showing a count of zero is honest, and the empty state behind
 * it already says "be the first — listing is free". What is not acceptable is
 * a tile that looks populated and leads to nothing, so the count is on the tile
 * rather than discovered after the tap.
 *
 * One hue for the whole grid, from the section it sits in. Twenty-eight tiles
 * in twenty-eight colours is a fruit salad; twenty-eight in one is a palette.
 */

export interface GridCategory {
  slug: string;
  name: string;
  /** How many live things are in it. Zero is shown, not hidden. */
  count: number;
}

export function CategoryGrid(
  { section, categories, active, onPick, emptyLabel }: {
    section: Section;
    categories: GridCategory[];
    /** The slug currently filtered on, so the grid can show where you are. */
    active: string;
    onPick: (slug: string) => void;
    /** What a zero count is called here — "none yet" reads better than "0". */
    emptyLabel?: string;
  },
) {
  if (categories.length === 0) return null;

  return (
    <nav className={`cat-grid sect-${section}`} aria-label="Categories">
      {categories.map(c => {
        const on = active === c.slug;
        return (
          <button
            key={c.slug}
            type="button"
            className={`cat-tile${on ? ' on' : ''}${c.count === 0 ? ' cat-empty' : ''}`}
            aria-pressed={on}
            /* Choosing the category you are already in clears it, so the grid
               is a toggle rather than a one-way door with no way back. */
            onClick={() => onPick(on ? '' : c.slug)}
          >
            <span className="cat-icon" aria-hidden>{categoryIcon(c.slug)}</span>
            <span className="cat-name">{c.name}</span>
            <span className="cat-count">
              {c.count > 0 ? c.count : (emptyLabel ?? 'none yet')}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
